import { Box, Flex, Text } from '@chakra-ui/react';
import { AlertTriangle } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { logError } from './logger';
import type { FlowConfig, ImporterStoreState, StoreHook } from './types';
import {
  Banner,
  CenteredScreen,
  ConnectingScreen,
  ConnectionErrorScreen,
  ErrorScreen,
  Footer,
  Header,
  ProcessingScreen,
  SettingsScreen,
  SuccessScreen,
  UploadScreen,
} from './screens';

/**
 * The shared multi-screen import flow: connecting → upload → settings →
 * processing → success/error. Generic over the app's Zustand store `S` (which
 * must satisfy {@link ImporterStoreState}); each app passes its store hook and a
 * {@link FlowConfig} carrying its labels + settings controls.
 */
export function ImporterFlow<S extends ImporterStoreState>({
  store,
  config,
}: {
  store: StoreHook<S>;
  config: FlowConfig;
}) {
  const isReady = store((s) => s.isReady);
  const connectionError = store((s) => s.connectionError);
  const skewMessage = store((s) => s.skewMessage);
  const file = store((s) => s.file);
  const status = store((s) => s.status);
  const progress = store((s) => s.progress);
  const message = store((s) => s.message);
  const notice = store((s) => s.notice);
  const setFile = store((s) => s.setFile);
  const startImport = store((s) => s.startImport);
  const cancel = store((s) => s.cancel);
  const reset = store((s) => s.reset);
  const retryConnection = store((s) => s.retryConnection);

  // Log import failures once each (the store turns them into UI state, so the
  // global handlers never see them). Reset when we leave the error state.
  const loggedRef = useRef<string | null>(null);
  useEffect(() => {
    if (status === 'error' && message && loggedRef.current !== message) {
      loggedRef.current = message;
      logError(message, { app: config.app, phase: 'import' });
    } else if (status !== 'error') {
      loggedRef.current = null;
    }
  }, [status, message, config.app]);

  if (connectionError) {
    return (
      <ConnectionErrorScreen message={connectionError} onRetry={retryConnection} />
    );
  }
  if (skewMessage) {
    return (
      <CenteredScreen>
        <Box color="figma.warning">
          <AlertTriangle size={28} />
        </Box>
        <Text fontSize="sm" color="figma.textSecondary" maxW="320px">
          {skewMessage}
        </Text>
      </CenteredScreen>
    );
  }
  if (!isReady) {
    return <ConnectingScreen />;
  }

  // "Import another" / "Start over" must return to the dropzone — clear the file
  // too, otherwise routing falls back to the settings screen for the same file.
  const startOver = () => {
    reset();
    setFile(null);
  };

  let content: ReactNode;
  if (status === 'done') {
    content = <SuccessScreen message={message ?? 'Done.'} onReset={startOver} />;
  } else if (status === 'error') {
    content = (
      <ErrorScreen message={message ?? 'Something went wrong.'} onReset={startOver} />
    );
  } else if (status === 'converting' || status === 'placing') {
    content = (
      <ProcessingScreen
        fileName={file?.name ?? ''}
        message={message}
        progress={progress}
        onCancel={cancel}
      />
    );
  } else if (file) {
    content = (
      <SettingsScreen
        fileName={file.name}
        settings={config.settings}
        importLabel={config.importLabel ?? 'Import'}
        onChangeFile={() => setFile(null)}
        onImport={startImport}
      />
    );
  } else {
    content = (
      <UploadScreen
        accept={config.accept}
        dropHint={config.dropHint}
        subhint={config.dropSubhint}
        onFile={(f) => setFile(f)}
      />
    );
  }

  const showNotice = !!notice && status === 'idle' && !file;

  return (
    <Flex direction="column" h="100%" minH="0" bg="figma.bg">
      <Header title={config.title} subtitle={config.subtitle} />
      {showNotice && <Banner text={notice} tone="warning" />}
      <Box flex="1" minH="0" overflow="hidden">
        {content}
      </Box>
      <Footer label={config.footerLabel} />
    </Flex>
  );
}
